/**
 * Simple in-memory cache for agent data
 * This helps avoid multiple API calls for the same agent within a session
 */

interface CachedAgent {
  id: string;
  display_name: string;
  parent?: string | null; // null = root primary agent, string = sub-agent
  parent_display_name?: string | null;
  timestamp: number;
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const agentCache = new Map<string, CachedAgent>();

/**
 * Get cached agent data by ID
 */
export function getCachedAgent(id: string): CachedAgent | null {
  const cached = agentCache.get(id);
  if (cached && Date.now() - cached["timestamp"] < CACHE_DURATION) {
    return cached;
  }
  // Remove expired entry
  agentCache.delete(id);
  return null;
}

/**
 * Set agent data in cache
 */
export function setCachedAgent(id: string, agent: Omit<CachedAgent, "timestamp">) {
  agentCache.set(id, {
    ...agent,
    timestamp: Date.now()
  });
}

/**
 * Clear specific agent cache
 */
export function clearAgentCache(id: string) {
  agentCache.delete(id);
}

/**
 * Clear all agent cache
 */
export function clearAllAgentCache() {
  agentCache.clear();
}

/**
 * Get agent display name from cache or return a placeholder
 * The breadcrumb menu config will provide the appropriate fallback based on the route
 */
export function getAgentDisplayName(id: string): string {
  const cached = getCachedAgent(id);
  if (cached?.display_name) {
    return cached.display_name;
  }
  // Return empty string - let the breadcrumb menu config handle the fallback
  // The route-level breadcrumb config decides how to label root agents vs sub-agents.
  return "";
}
